import {
  buildCountryOptionKey,
  findCountryByOptionKey,
  normalizeCountryList,
} from '@formosa/shared/utils/countryUtils';

/** KYC 欄位預設標籤（對應後端 KycConfig.optionList） */
export const KYC_FIELD_LABELS = {
  identityNumber: '證件號碼',
  certType: '證件類型',
  birthday: '生日',
  gender: '性別',
  country: '國家/地區',
  image: '證件照片上傳',
  realName: '名字',
  lastName: '姓氏',
};

export const KYC_CERT_TYPE_OPTIONS = [
  { value: 1, labelKey: 'kyc.certType.idCard' },
  { value: 2, labelKey: 'kyc.certType.passport' },
  { value: 3, labelKey: 'kyc.certType.driverLicense' },
];

export const KYC_GENDER_OPTIONS = [
  { value: 1, labelKey: 'kyc.gender.male' },
  { value: 2, labelKey: 'kyc.gender.female' },
];

export {
  buildCountryOptionKey,
  normalizeCountryList,
  findCountryByOptionKey,
};

export const REAL_NAME_STATUS = {
  NOT_CERTIFIED: 'NOT_CERTIFIED',
  AUDITING: 'AUDITING',
  VERIFIED: 'VERIFIED',
  FAILED: 'FAILED',
};

/** 正規化後端 Enum／字串狀態 */
export function normalizeRealNameStatus(status) {
  if (!status) {
    return REAL_NAME_STATUS.NOT_CERTIFIED;
  }
  if (typeof status === 'string') {
    return status;
  }
  return status.name || status.code || REAL_NAME_STATUS.NOT_CERTIFIED;
}

export function resolveKycStatusLabelKey(status) {
  switch (normalizeRealNameStatus(status)) {
    case REAL_NAME_STATUS.VERIFIED:
      return 'kyc.status.verified';
    case REAL_NAME_STATUS.AUDITING:
      return 'kyc.status.auditing';
    case REAL_NAME_STATUS.FAILED:
      return 'kyc.status.failed';
    default:
      return 'kyc.status.notCertified';
  }
}

export function canSubmitKyc(status) {
  const normalized = normalizeRealNameStatus(status);
  return normalized === REAL_NAME_STATUS.NOT_CERTIFIED || normalized === REAL_NAME_STATUS.FAILED;
}

export function resolveKycActionLabelKey(status) {
  return normalizeRealNameStatus(status) === REAL_NAME_STATUS.FAILED
    ? 'kyc.reVerify'
    : 'kyc.goVerify';
}

export function isKycVerified(status) {
  return normalizeRealNameStatus(status) === REAL_NAME_STATUS.VERIFIED;
}

function resolveCertTypeLabel(certType, t) {
  const item = KYC_CERT_TYPE_OPTIONS.find((option) => option.value === Number(certType));
  return item ? t(item.labelKey) : (certType ?? '—');
}

function resolveGenderLabel(gender, t) {
  const item = KYC_GENDER_OPTIONS.find((option) => option.value === Number(gender));
  return item ? t(item.labelKey) : (gender ?? '—');
}

/** 依 tenant usedList 組裝唯讀認證內容列（不展示證件圖片） */
export function buildKycInfoRows(detail, usedList, t) {
  if (!detail || !Array.isArray(usedList) || usedList.length === 0) {
    return [];
  }

  return usedList
    .filter((key) => KYC_FIELD_LABELS[key])
    .map((key) => {
      let label = t(`kyc.fields.${key}`);
      let value = '—';

      switch (key) {
        case 'country':
          label = t('kyc.info.country');
          value = detail.areaName || detail.areaCode || '—';
          break;
        case 'image':
          label = t('kyc.info.uploadIdPhoto');
          value = t('kyc.imageVerified');
          break;
        case 'identityNumber':
          value = detail.identityNumber || '—';
          break;
        case 'certType':
          value = resolveCertTypeLabel(detail.certType, t);
          break;
        case 'birthday':
          value = detail.birthday ? String(detail.birthday).slice(0, 10) : '—';
          break;
        case 'gender':
          value = resolveGenderLabel(detail.gender, t);
          break;
        case 'realName':
          value = detail.realName || '—';
          break;
        case 'lastName':
          value = detail.lastName || '—';
          break;
        default:
          break;
      }

      return { key, label, value };
    });
}

export function isBasicAuthVerified(member) {
  if (!member) {
    return false;
  }
  if (member.registerVerifyStatus === 'VERIFIED') {
    return true;
  }
  return Boolean(member.email?.trim() || member.mobile?.trim());
}

export function buildEmptyKycForm(usedList = []) {
  const form = {
    identityNumber: '',
    certType: '',
    birthday: '',
    gender: '',
    areaCode: '',
    areaName: '',
    lastName: '',
    realName: '',
    identityImageFront: '',
    identityImageBack: '',
    countryKey: '',
  };
  if (!Array.isArray(usedList)) {
    return form;
  }
  return form;
}

/** 以既有認證資料預填表單（重新認證時使用） */
export function buildKycFormFromDetail(detail, countries = []) {
  const form = buildEmptyKycForm();
  if (!detail) {
    return form;
  }

  let countryKey = '';
  if (detail.areaCode || detail.areaName) {
    const match = countries.find(
      (item) => item.areaCode === detail.areaCode && item.zhName === detail.areaName
    ) || countries.find((item) => item.areaCode === detail.areaCode);
    if (match) {
      countryKey = buildCountryOptionKey(match);
    }
  }

  const birthday = detail.birthday ? String(detail.birthday).slice(0, 10) : '';

  return {
    ...form,
    identityNumber: detail.identityNumber || '',
    certType: detail.certType != null ? String(detail.certType) : '',
    birthday,
    gender: detail.gender != null ? String(detail.gender) : '',
    areaCode: detail.areaCode || '',
    areaName: detail.areaName || '',
    lastName: detail.lastName || '',
    realName: detail.realName || '',
    identityImageFront: detail.identityImageFront || '',
    identityImageBack: detail.identityImageBack || '',
    countryKey,
  };
}

export function buildKycSubmitPayload(form, usedList) {
  const payload = {};
  (usedList || []).forEach((key) => {
    switch (key) {
      case 'identityNumber':
        payload.identityNumber = form.identityNumber?.trim();
        break;
      case 'certType':
        payload.certType = form.certType === '' ? null : Number(form.certType);
        break;
      case 'birthday':
        payload.birthday = form.birthday || null;
        break;
      case 'gender':
        payload.gender = form.gender === '' ? null : Number(form.gender);
        break;
      case 'country':
        payload.areaCode = form.areaCode?.trim();
        payload.areaName = form.areaName?.trim();
        break;
      case 'image':
        payload.identityImageFront = form.identityImageFront?.trim();
        payload.identityImageBack = form.identityImageBack?.trim();
        break;
      case 'realName':
        payload.realName = form.realName?.trim();
        break;
      case 'lastName':
        payload.lastName = form.lastName?.trim();
        break;
      default:
        break;
    }
  });
  return payload;
}

/** 必填欄位是否已全部填寫（供提交按鈕亮起） */
export function isKycFormComplete(form, usedList) {
  if (!Array.isArray(usedList) || usedList.length === 0) {
    return false;
  }
  return Object.keys(validateKycForm(form, usedList, () => '')).length === 0;
}

export function validateKycForm(form, usedList, t) {
  const errors = {};
  (usedList || []).forEach((key) => {
    switch (key) {
      case 'identityNumber':
        if (!form.identityNumber?.trim()) {
          errors.identityNumber = t('kyc.validation.required');
        }
        break;
      case 'certType':
        if (form.certType === '' || form.certType == null) {
          errors.certType = t('kyc.validation.required');
        }
        break;
      case 'birthday':
        if (!form.birthday) {
          errors.birthday = t('kyc.validation.required');
        }
        break;
      case 'gender':
        if (form.gender === '' || form.gender == null) {
          errors.gender = t('kyc.validation.required');
        }
        break;
      case 'country':
        if (!form.areaCode?.trim() || !form.areaName?.trim()) {
          errors.country = t('kyc.validation.required');
        }
        break;
      case 'image':
        if (!form.identityImageFront?.trim() || !form.identityImageBack?.trim()) {
          errors.image = t('kyc.validation.imageBoth');
        }
        break;
      case 'realName':
        if (!form.realName?.trim()) {
          errors.realName = t('kyc.validation.required');
        }
        break;
      case 'lastName':
        if (!form.lastName?.trim()) {
          errors.lastName = t('kyc.validation.required');
        }
        break;
      default:
        break;
    }
  });
  return errors;
}
